import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface IndustryDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
}

export default function IndustryDropdown({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select industry...',
  disabled = false 
}: IndustryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term and show all results
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options
        .filter(option => 
          option.toLowerCase().includes(searchTerm.toLowerCase())
        );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
  };

  const displayValue = value || placeholder;
  const hasValue = Boolean(value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`
          w-full px-3 py-2 rounded-lg border border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-900
          focus-within:outline-none focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 
          dark:text-white cursor-pointer flex items-center justify-between
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-zinc-400 dark:hover:border-zinc-500'}
          ${hasValue ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 dark:text-zinc-400'}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="truncate">{displayValue}</span>
        <div className="flex items-center space-x-1">
          {hasValue && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
            >
              <X size={14} className="text-zinc-400" />
            </button>
          )}
          <ChevronDown 
            size={16} 
            className={`text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-30 w-full mt-1 rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900">
          <div className="p-2 border-b border-zinc-200 dark:border-zinc-800">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search industries..."
              className="w-full px-2 py-1 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className="px-3 py-2 text-sm cursor-pointer text-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 dark:text-white"
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                No industries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 